import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import { testimonials } from "@/mocks/testimonials";

const stats = [
  { value: "50万+", label: "已生成图像" },
  { value: "10万+", label: "活跃用户" },
  { value: "99.7%", label: "用户满意度" },
  { value: "24/7", label: "服务支持" },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const [formMsg, setFormMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/d9pmtu9n2trhb35gvi6g",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(
            Array.from(formData.entries())
              .filter(([k]) => k !== "phone_alt")
              .map(([k, v]) => [k, v as string])
          ).toString(),
        }
      );
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        // not json
      }
      if (res.ok && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        (form as HTMLFormElement).reset();
      } else {
        const msg =
          ((parsed as { meta?: { message?: string } }).meta?.message) ||
          ((parsed as { message?: string }).message) ||
          text;
        setFormStatus("error");
        setFormMsg(
          String(msg).includes("spam")
            ? "提交失败，请稍后再试。"
            : String(msg) || "提交失败，请稍后再试。"
        );
      }
    } catch {
      setFormStatus("error");
      setFormMsg("网络错误，请检查连接后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 - AI美女生成平台",
            description:
              "AI美女生成平台致力于将最先进的AI图像生成技术，转化为人人都能轻松使用的创作工具。",
            url: "https://ai-beauty-platform.com/about",
            isPartOf: {
              "@type": "WebSite",
              name: "AI美女生成平台",
              url: "https://ai-beauty-platform.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://ai-beauty-platform.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "关于我们",
                },
              ],
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link
                to="/"
                className="hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20creative%20workspace%20with%20warm%20lighting%2C%20elegant%20interior%20design%2C%20clean%20desk%20with%20monitor%20displaying%20digital%20art%2C%20soft%20cream%20and%20beige%20tones%2C%20inspirational%20atmosphere%2C%20high%20quality%20interior%20photography%2C%20no%20people&width=1600&height=600&seq=about-hero&orientation=landscape"
            alt="关于我们 - AI美女生成平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex items-center justify-center px-4 md:px-6">
            <div className="text-center max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                关于我们
              </h1>
              <p className="text-white/80 leading-relaxed">
                用AI技术让每个人都能轻松创造美丽
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {s.value}
                  </p>
                  <p className="text-sm text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
              品牌故事
            </h2>
            <div className="space-y-6 text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-950">
                  AI美学生成平台
                </strong>{" "}
                创立于2024年，由一群热爱AI技术与视觉艺术的技术专家和设计师共同打造。我们的使命是
                <strong>让AI图像生成技术走入每个人的生活</strong>
                ，让没有设计基础的用户也能轻松创作出专业水准的AI美女图像。
              </p>
              <p>
                在过去两年间，我们的平台已累计为超过10万用户生成了50万+张高质量的
                <strong>AI美女图像</strong>
                ，涵盖写真、头像、壁纸、虚拟角色等多种类别。用户的每一次创作，都是对我们技术实力和产品理念的认可。
              </p>
              <p>
                我们持续投入研发资源，追踪最新的AI图像生成技术，不断优化模型效果和用户体验。我们的愿景是成为
                <strong>全球领先的AI美女创作平台</strong>
                ，让AI之美触手可及。
              </p>
            </div>
          </div>
        </section>

        {/* User Feedback */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-12 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-4 h-4 flex items-center justify-center"
                      >
                        <i
                          className={`ri-star-fill text-sm ${
                            i < t.rating
                              ? "text-accent-500"
                              : "text-secondary-200"
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    {t.content}
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">
                        {t.name}
                      </p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                有任何问题或建议？我们很乐意听取你的声音。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">
                  邮箱
                </h3>
                <p className="text-xs text-foreground-500">
                  contact@ai-beauty-platform.com
                </p>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100/70 text-accent-600">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">
                  微信
                </h3>
                <p className="text-xs text-foreground-500">AI美学生成平台</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-secondary-100/70 text-secondary-600">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">
                  工作时间
                </h3>
                <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>

            {formStatus === "success" ? (
              <div className="text-center p-10 rounded-2xl bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  消息已发送！
                </h3>
                <p className="text-sm text-foreground-600">
                  感谢你的留言，我们会在1-2个工作日内回复你。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-honeypot"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="contact_name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact_email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact_subject"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact_subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact_message"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="contact_message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>
                {formStatus === "error" && formMsg && (
                  <p className="text-sm text-red-500">{formMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer font-medium disabled:opacity-60"
                >
                  {submitting ? "发送中..." : "发送消息"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}